import React, { useRef, useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import SignatureCanvas from 'react-signature-canvas';

export default function SignatureCapture({ label, onEnd, value, height = 150 }) {
  const sigRef = useRef(null);
  const containerRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(0);

  // Measure container width and resize canvas accordingly
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setCanvasWidth(containerRef.current.offsetWidth - 4);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Load saved signature into canvas when value is provided on mount
  useEffect(() => {
    if (sigRef.current && value && canvasWidth > 0) {
      sigRef.current.fromDataURL(value, { width: canvasWidth, height: height - 4 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasWidth]);

  const handleClear = useCallback(() => {
    if (sigRef.current) {
      sigRef.current.clear();
    }
    if (onEnd) onEnd('');
  }, [onEnd]);

  const handleEnd = useCallback(() => {
    if (sigRef.current && onEnd) {
      const dataUrl = sigRef.current.getCanvas().toDataURL('image/png');
      onEnd(dataUrl);
    }
  }, [onEnd]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}
      <IconButton
        size="small"
        onClick={handleClear}
        sx={{ position: 'absolute', top: label ? 28 : 0, right: 0, color: 'error.main', zIndex: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Box
        ref={containerRef}
        sx={{
          border: '2px solid',
          borderColor: value ? 'success.main' : 'divider',
          borderRadius: 1,
          bgcolor: '#fafafa',
          width: '100%',
          height: height,
        }}
      >
        {canvasWidth > 0 && (
          <SignatureCanvas
            ref={sigRef}
            penColor="black"
            canvasProps={{
              width: canvasWidth,
              height: height - 4,
              style: { cursor: 'crosshair' },
            }}
            onEnd={handleEnd}
          />
        )}
      </Box>
    </Box>
  );
}
